import { isIndependentResponse, type MultiplicationAttempt } from "./attempt.ts";
import { buildFluencyBaseline } from "./fluency.ts";
import { evaluateTableFactMastery, type TableFactMastery } from "./mastery.ts";
import {
  MULTIPLICATION_CATALOG,
  type MultiplicationFact,
  type MultiplicationFactId,
} from "./model.ts";

export type TableSessionBucket = "WEAK" | "DISCOVER" | "SUCCESS";

export interface TableSelection {
  readonly fact: MultiplicationFact;
  readonly bucket: TableSessionBucket;
  readonly reason: string;
}

export interface SelectNextTableFactInput {
  readonly attempts: readonly MultiplicationAttempt[];
  readonly recentFactIds: readonly MultiplicationFactId[];
  readonly sessionFactIds: readonly MultiplicationFactId[];
  readonly itemIndex: number;
  readonly now: string;
  readonly random?: () => number;
}

const CALIBRATION_FACT_TARGET = 12;

const CALIBRATION_PATTERN: readonly TableSessionBucket[] = [
  "DISCOVER", "DISCOVER", "WEAK", "SUCCESS", "DISCOVER",
  "WEAK", "SUCCESS", "DISCOVER", "WEAK", "SUCCESS",
  "DISCOVER", "WEAK", "SUCCESS", "DISCOVER", "WEAK",
];

const PRACTICE_PATTERN: readonly TableSessionBucket[] = [
  "WEAK", "SUCCESS", "WEAK", "DISCOVER", "SUCCESS",
  "WEAK", "SUCCESS", "WEAK", "DISCOVER", "SUCCESS",
  "WEAK", "SUCCESS", "WEAK", "DISCOVER", "SUCCESS",
];

export function tableSessionBucket(
  itemIndex: number,
  attemptedFactCount: number,
): TableSessionBucket {
  const pattern = attemptedFactCount < CALIBRATION_FACT_TARGET
    ? CALIBRATION_PATTERN
    : PRACTICE_PATTERN;
  return pattern[itemIndex % pattern.length];
}

function latestIndependent(
  factId: MultiplicationFactId,
  attempts: readonly MultiplicationAttempt[],
): MultiplicationAttempt | null {
  return [...attempts]
    .reverse()
    .find((attempt) => attempt.factId === factId && isIndependentResponse(attempt)) ?? null;
}

function tableSeenCount(
  left: number,
  sessionFactIds: readonly MultiplicationFactId[],
): number {
  return sessionFactIds.reduce((count, id) => {
    return count + (MULTIPLICATION_CATALOG.factById.get(id)?.left === left ? 1 : 0);
  }, 0);
}

function isAnchorFact(fact: MultiplicationFact): boolean {
  return [1, 2, 5, 10].includes(fact.left) || [1, 2, 5, 10].includes(fact.right);
}

function scoreFact(
  fact: MultiplicationFact,
  mastery: TableFactMastery,
  bucket: TableSessionBucket,
  attempts: readonly MultiplicationAttempt[],
  sessionFactIds: readonly MultiplicationFactId[],
  random: () => number,
): number {
  const latest = latestIndependent(fact.id, attempts);
  const coverageBonus = bucket === "DISCOVER"
    ? Math.max(0, 5 - tableSeenCount(fact.left, sessionFactIds)) * 8
    : 0;
  const consolidationBonus = mastery.currentStage === "LEARNING"
    || mastery.currentStage === "PRACTISING"
    ? Math.min(mastery.independentResponseCount, 4) * 35
      + (mastery.sessionCount === 1 && mastery.independentResponseCount >= 3 ? 60 : 0)
    : 0;
  let score = random() * 5 + coverageBonus;

  if (bucket === "DISCOVER") {
    score += mastery.currentStage === "UNSEEN" ? 140 : 0;
    score += latest === null ? 25 : 0;
  } else if (bucket === "WEAK") {
    score += latest?.correct === false ? 300 : 0;
    score += mastery.retentionState === "REFRESH_DUE" ? 180 : 0;
    score += mastery.retentionState === "DUE" ? 130 : 0;
    score += mastery.currentStage === "LEARNING" ? 90 : 0;
    score += mastery.currentStage === "PRACTISING" ? 150 : 0;
    score += mastery.currentStage === "UNSEEN" ? 5 : 0;
    score += consolidationBonus;
  } else {
    score += latest?.correct === true ? 100 : 0;
    score += isAnchorFact(fact) ? 25 : 0;
    score += mastery.currentStage === "LEARNING" ? 80 : 0;
    score += mastery.currentStage === "PRACTISING" ? 160 : 0;
    score += mastery.currentStage === "FLUENT" || mastery.currentStage === "MASTERED" ? 50 : 0;
    score += mastery.currentStage === "UNSEEN" && isAnchorFact(fact) ? 35 : 0;
    score += consolidationBonus;
  }

  return score;
}

export function selectNextTableFact(input: SelectNextTableFactInput): TableSelection {
  const random = input.random ?? Math.random;
  const independentFactIds = new Set(
    input.attempts.filter(isIndependentResponse).map((attempt) => attempt.factId),
  );
  const baseline = buildFluencyBaseline(input.attempts);
  const requestedBucket = tableSessionBucket(input.itemIndex, independentFactIds.size);
  const cooldown = new Set(input.recentFactIds.slice(-3));
  const recentFamilies = new Set(
    input.recentFactIds.slice(-2).map((id) => MULTIPLICATION_CATALOG.factById.get(id)?.familyId),
  );
  const masteryByFact = new Map(
    MULTIPLICATION_CATALOG.facts.map((fact) => [
      fact.id,
      evaluateTableFactMastery(fact.id, input.attempts, input.now, baseline),
    ]),
  );

  const strictCandidates = MULTIPLICATION_CATALOG.facts.filter((fact) => {
    return !cooldown.has(fact.id) && !recentFamilies.has(fact.familyId);
  });
  const candidates = strictCandidates.length > 0
    ? strictCandidates
    : MULTIPLICATION_CATALOG.facts.filter((fact) => !cooldown.has(fact.id));

  const scored = candidates.map((fact) => ({
    fact,
    mastery: masteryByFact.get(fact.id)!,
    score: scoreFact(
      fact,
      masteryByFact.get(fact.id)!,
      requestedBucket,
      input.attempts,
      input.sessionFactIds,
      random,
    ),
  })).sort((a, b) => b.score - a.score);

  const chosen = scored[0];
  if (!chosen) throw new Error("De tafelcatalogus bevat geen selecteerbare feiten.");

  const reason = requestedBucket === "DISCOVER"
    ? chosen.mastery.currentStage === "UNSEEN" ? "nieuw tafelfeit" : "verdere dekking"
    : requestedBucket === "SUCCESS"
      ? "vertrouwensvraag"
      : chosen.mastery.retentionState === "DUE" ? "onderhoud" : "gericht oefenen";

  return { fact: chosen.fact, bucket: requestedBucket, reason };
}
